import { Link } from '@tanstack/react-router'
import { Glass } from '@ui/glass/Glass'
import './tab-bar.css'
import { TABS } from './tabs'
import { IconeCasa, IconeEtiqueta, IconeFluxo, IconeLista } from './icones'

const ICONES = {
  casa: IconeCasa,
  fluxo: IconeFluxo,
  etiqueta: IconeEtiqueta,
  lista: IconeLista,
} as const

/**
 * As quatro abas, em ícone — e a selecionada se abre num rótulo.
 *
 * ## Por que não é ícone puro, nem ícone com rótulo sempre
 *
 * Ícone puro é ilegível: "etiqueta" e "lista" não dizem sozinhos o que são, e
 * quem abre o app uma vez por semana reaprende toda vez. Rótulo sempre gasta a
 * largura de quatro palavras numa barra de 375px e obriga o texto a encolher
 * até o tamanho em que ele deixa de ser lido de qualquer jeito.
 *
 * A saída é a **aba ativa carregar o rótulo**: ela é a única que precisa dizer
 * onde você está, e as outras três precisam apenas ser alcançáveis. De quebra,
 * é o que faz o destaque — a pílula que cresce é mais visível que uma cor
 * diferente, inclusive para quem não distingue as cores.
 *
 * ## O rótulo existe SEMPRE para quem usa leitor de tela
 *
 * Ele some por **largura**, e não por `display` ou `aria-label`: o texto é o
 * mesmo nos dois estados, então o nome acessível não muda quando a aba ativa
 * troca — e não há um segundo lugar onde alguém possa esquecer de atualizar a
 * tradução.
 *
 * ## A animação é do CSS, e não de estado
 *
 * Quem manda é o `aria-current` que o router já escreve. Sem estado novo, sem
 * medir posição, sem efeito — e sem o risco de a barra discordar da rota, que
 * é o defeito clássico de um indicador controlado à mão. Ver `tab-bar.css`.
 *
 * ## `.alvo` no `activeProps` também
 *
 * `activeProps.className` **substitui** o `className` base no TanStack Router,
 * não soma. A versão anterior repetia as classes à mão e deixava o `.alvo` de
 * fora — a aba ativa era a única sem os 44px, justamente a que se toca para
 * recarregar a tela em que já se está.
 */
export function TabBar() {
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-20 px-3"
      style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
    >
      <Glass level="frost" className="mx-auto flex max-w-md items-center gap-1 p-1.5">
        {TABS.map((tab) => {
          const Icone = ICONES[tab.icone]
          return (
            <Link
              key={tab.to}
              to={tab.to}
              className={INATIVA}
              // `includeSearch: false` — e sem isto a aba NÃO acende quando a
              // URL tem query. O TanStack compara a busca por padrão, então
              // `/transacoes?mes=2026-08` (o estado normal daquela tela) não
              // casava com o `to="/transacoes"` do link, e a barra ficava sem
              // nenhuma aba marcada. Achado no modo demo, cuja URL carrega
              // `?demo` — mas o defeito era de produção.
              activeOptions={{ exact: tab.to === '/', includeSearch: false }}
              activeProps={{ 'aria-current': 'page', className: ATIVA }}
            >
              <Icone />
              {/*
                O texto está SEMPRE no DOM, e some por LARGURA — ver
                `tab-bar.css`. Quem o esconde é o CSS a partir do
                `aria-current` que o router já põe: sem uma segunda fonte de
                verdade sobre qual aba está ativa, e sem o risco de a barra
                discordar da rota.
              */}
              <span className="rotulo-da-aba">{tab.label}</span>
            </Link>
          )
        })}
      </Glass>
    </nav>
  )
}

/** A base: quadrada, ícone centrado, rótulo escondido. */
const INATIVA =
  'aba alvo flex-1 gap-1.5 overflow-hidden rounded-full px-2 text-xs font-medium ' +
  'text-[var(--text-muted)]'

/**
 * A ativa: cresce, ganha o fundo da marca e mostra o rótulo.
 *
 * `flex-[2]` e não uma largura fixa: a pílula acomoda "Transações" e
 * "Dashboard" sem número mágico, e as outras três dividem o que sobra.
 */
const ATIVA =
  'aba alvo flex-[2] gap-1.5 overflow-hidden rounded-full ' +
  'bg-[color-mix(in_oklab,var(--brand)_22%,transparent)] px-3 text-xs font-semibold ' +
  'text-[var(--brand)] ring-1 ring-[color-mix(in_oklab,var(--brand)_38%,transparent)]'
